import { useState } from 'react';
import { money } from '@/lib/format';
import {
  centsToInput,
  claimedCents,
  emptyDraft,
  evenShares,
  priceDrafts,
  type SplitDraft,
} from '@/lib/split';
import type { TransactionSplit } from '@/lib/types';
import { Button, Toggle, cx, fieldStyles } from './ui';
import s from './SplitEditor.module.css';

/**
 * Who owes what of a payment you made.
 *
 * Deliberately controlled and unaware of saving: the record form holds rows
 * until the transaction exists, and the transaction detail posts them straight
 * away — both need the same arithmetic and the same running tally, and neither
 * should have its own idea of what an even split is.
 */
export function SplitEditor({
  rows,
  onChange,
  totalCents,
  existing = [],
  currency,
}: {
  rows: SplitDraft[];
  onChange: (rows: SplitDraft[]) => void;
  /** Magnitude of the payment being split. */
  totalCents: number;
  /** Shares already saved against this payment, which the new ones sit beside. */
  existing?: TransactionSplit[];
  currency: string;
}) {
  // Whether an even split leaves a share for you. Off means the whole payment
  // was someone else's — a round you covered and get back in full.
  const [includeMe, setIncludeMe] = useState(true);

  const spokenFor = claimedCents(existing);
  const divisible = Math.max(0, totalCents - spokenFor);
  // Blanks are priced the way saving will price them, so the tally and the
  // greyed figure in each empty box say what is actually about to happen.
  const priced = priceDrafts(rows, totalCents, existing);
  const theirs = priced.reduce((total, cents) => total + cents, 0);
  const yours = totalCents - spokenFor - theirs;

  function update(index: number, patch: Partial<SplitDraft>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  /** Divides what's left between the rows — and you, unless told otherwise. */
  function splitEvenly() {
    if (rows.length === 0) return;
    const shares = evenShares(divisible, rows.length + (includeMe ? 1 : 0));
    // Yours is the last share, so the odd penny lands on someone who owes,
    // never on the row that isn't there.
    onChange(rows.map((row, i) => ({ ...row, amount: centsToInput(shares[i] ?? 0) })));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className={s.rows}>
        {rows.map((row, i) => (
          <div key={row.key} className={s.row}>
            <input
              className={cx(fieldStyles.input, s.rowName)}
              value={row.name}
              onChange={(e) => update(i, { name: e.target.value })}
              placeholder="Who owes you?"
              aria-label={`Person ${i + 1}`}
            />
            <input
              className={fieldStyles.inputMono}
              value={row.amount}
              onChange={(e) => update(i, { amount: e.target.value })}
              // An empty box is an equal share, so show the figure it would
              // take rather than a meaningless 0.00.
              placeholder={centsToInput(priced[i] ?? 0)}
              inputMode="decimal"
              aria-label={`${row.name.trim() || `Person ${i + 1}`} owes`}
            />
            <Button
              variant="ghost"
              className={s.drop}
              aria-label={`Remove ${row.name.trim() || `person ${i + 1}`}`}
              onClick={() => onChange(rows.filter((_, index) => index !== i))}
            >
              ×
            </Button>
          </div>
        ))}
      </div>

      <div className={s.tools}>
        <Button size="sm" variant="ghost" onClick={() => onChange([...rows, emptyDraft()])}>
          ＋ Add person
        </Button>
        <Button size="sm" variant="ghost" disabled={rows.length === 0} onClick={splitEvenly}>
          Split evenly
        </Button>
        <span className={s.toolNote} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7 }}>
          my share included
          <Toggle checked={includeMe} onChange={setIncludeMe} label="Include my own share when splitting evenly" />
        </span>
      </div>

      {rows.length > 0 ? (
        <div className={s.toolNote}>
          Leave an amount blank and that person takes an equal share of what’s left.
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className={s.tally}>
          <span>
            they owe <span className={s.tallyFigure}>{money(theirs, currency, true)}</span>
          </span>
          <span>
            {yours < 0 ? 'over by ' : 'your share '}
            <span className={cx(s.tallyFigure, yours < 0 && s.tallyOver)}>
              {money(Math.abs(yours), currency, true)}
            </span>
          </span>
        </div>
      ) : null}
    </div>
  );
}
